"use client";

import { useTranslations } from "next-intl";
import type * as React from "react";

import { ConfirmDialog } from "@/components/confirm-dialog";

interface CancelPaymentConfirmModalProps {
  /**
   * Controlled-mode props. Required when `trigger` is omitted, optional when
   * a trigger is provided (Radix manages its own open state then).
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Fired when the user clicks the destructive "Cancel payment" button. The
   * caller is responsible for the mutation, navigation, and any close logic
   * — this modal does NOT auto-close after onConfirm because the mutation
   * may take a moment and we want the spinner to stay visible until the
   * parent decides to close (via onOpenChange or by unmounting).
   */
  onConfirm: () => void | Promise<void>;
  /**
   * When true, the destructive button is disabled and shows a spinner +
   * "Cancelling..." label. The Close button stays enabled so the user can
   * still bail out before the mutation resolves.
   */
  isPending?: boolean;
  /**
   * Optional trigger slot. When provided, wraps it in AlertDialogTrigger
   * and lets Radix manage open state internally (caller can omit `open` /
   * `onOpenChange`). When omitted, the modal is fully controlled.
   */
  trigger?: React.ReactNode;
}

export function CancelPaymentConfirmModal({
  open,
  onOpenChange,
  onConfirm,
  isPending = false,
  trigger,
}: CancelPaymentConfirmModalProps) {
  const t = useTranslations("pay.confirmationActions");
  const tCommon = useTranslations("common");

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      title={t("cancelConfirmTitle")}
      description={t("cancelConfirmDescription")}
      cancelLabel={tCommon("close")}
      confirmLabel={t("cancelPayment")}
      pendingLabel={t("cancelling")}
      onConfirm={onConfirm}
      destructive
      stayOpenOnConfirm
      isPending={isPending}
      cancelTestId="cancel-payment-modal-close"
      confirmTestId="cancel-payment-modal-confirm"
    />
  );
}
